/**
 * WeeklySummaryReport — "The Fridge Report"
 *
 * A one-page printable summary designed to be stuck on the fridge.
 * Shows spendable balance, bills due this week, budget status,
 * and recent transactions at a glance.
 *
 * Phase 9A
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserConfig, getActiveBills, getBudgetPeriod, getRecentTransactions,
  getPrimaryCheckingBalance,
} from '../../services/db';
import { calculateSpendable } from '../../utils/spendable';
import { calculateBudget, BUCKET_META, BUCKETS } from '../../utils/budget';
import { getDaysUntilPayday } from '../../utils/dates';
import { formatCurrency, formatDate, getMonthKey } from '../../utils/format';
import theme from '../../utils/theme';

export default function WeeklySummaryReport() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, bills, budget, transactions, checking] = await Promise.all([
        getUserConfig(user.id),
        getActiveBills(user.id),
        getBudgetPeriod(user.id, getMonthKey()),
        getRecentTransactions(user.id, 15),
        getPrimaryCheckingBalance(user.id),
      ]);

      const paydayAnchor = config?.payday_anchor || '2026-02-25';
      const income = config?.conservative_income || 8030;
      const spendable = calculateSpendable({
        checkingBalance: checking,
        bills,
        paydayAnchor,
      });

      // Bills due within next 7 days
      const today = new Date();
      const todayDay = today.getDate();
      const billsDueThisWeek = bills.filter(b => {
        const dueDay = b.due_day;
        // Simple 7-day lookahead within same month
        const diff = dueDay - todayDay;
        return diff >= 0 && diff <= 7;
      });

      const budgetData = budget ? calculateBudget({
        income,
        needActual: budget.need_actual || 0,
        wantActual: budget.want_actual || 0,
        savingsActual: budget.savings_actual || 0,
      }) : calculateBudget({ income });

      setData({
        spendable,
        bills,
        billsDueThisWeek,
        budgetData,
        transactions,
        daysUntilPayday: getDaysUntilPayday(paydayAnchor),
        income,
      });
    } catch (err) {
      console.error('Failed to load weekly report:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div className="report-content"><p className="empty-state">Loading report...</p></div>;
  }

  if (!data) {
    return <div className="report-content"><p className="empty-state">Unable to load report data.</p></div>;
  }

  const { spendable, billsDueThisWeek, budgetData, transactions, daysUntilPayday } = data;
  const today = new Date();

  return (
    <div className="report-content">
      {/* Print-only header */}
      <div className="report-header">
        <div className="report-header-title">Spendable — Weekly Summary</div>
        <div className="report-header-date">{formatDate(today, 'full')}</div>
      </div>

      {/* Spendable hero */}
      <div className="report-section">
        <div className="report-hero">
          <div className="report-hero-label">Spendable Balance</div>
          <div className={`report-hero-amount ${spendable.isNegative ? 'negative' : ''}`}>
            {formatCurrency(spendable.spendable, false)}
          </div>
          <div className="report-hero-meta">
            <span>{formatCurrency(spendable.dailyAllowance, false)}/day</span>
            <span>{daysUntilPayday} days to payday</span>
          </div>
        </div>
      </div>

      {/* Bills due this week */}
      <div className="report-section">
        <div className="report-section-title">Bills Due This Week</div>
        {billsDueThisWeek.length === 0 ? (
          <p className="empty-state">No bills due this week</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Due</th>
                <th className="text-right">Paid?</th>
              </tr>
            </thead>
            <tbody>
              {billsDueThisWeek.map(bill => (
                <tr key={bill.id}>
                  <td>{bill.name}</td>
                  <td className="text-right">{formatCurrency(bill.amount)}</td>
                  <td className="text-right">{bill.due_day}</td>
                  <td className="text-right">{bill.current_cycle_paid ? '\u2713' : '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Budget status */}
      <div className="report-section">
        <div className="report-section-title">Budget Status</div>
        {[BUCKETS.NEED, BUCKETS.WANT, BUCKETS.SAVINGS].map(bucket => {
          const b = budgetData.buckets[bucket];
          const meta = BUCKET_META[bucket];
          const pct = Math.min(b.percent, 100);
          const color = b.status === 'on-track' ? theme.success : b.status === 'warning' ? theme.warning : theme.danger;
          return (
            <div key={bucket} style={{ marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span>{meta.emoji} {meta.label}</span>
                <span style={{ fontWeight: 600 }}>
                  {formatCurrency(b.actual, false)} / {formatCurrency(b.target, false)}
                </span>
              </div>
              <div className="report-bar">
                <div className="report-bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent transactions */}
      <div className="report-section">
        <div className="report-section-title">Recent Transactions</div>
        {transactions.length === 0 ? (
          <p className="empty-state">No recent transactions</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(txn => (
                <tr key={txn.id}>
                  <td>{formatDate(txn.date)}</td>
                  <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {txn.description}
                  </td>
                  <td>{txn.category || '\u2014'}</td>
                  <td className="text-right" style={{ fontWeight: 600 }}>
                    {formatCurrency(txn.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="report-footer">
        Generated {formatDate(today, 'full')}
      </div>
    </div>
  );
}
